import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Sort a breakdown map by count descending, returning [{label, count}].
function sortedBreakdown(map) {
  return Object.entries(map || {})
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => ({ label, count }));
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

// Small bar showing proportion relative to a max value.
function MiniBar({ value, max, color = '#007bff' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <div style={{
        flex: 1,
        height: '10px',
        backgroundColor: '#e9ecef',
        borderRadius: '5px',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          backgroundColor: color,
          borderRadius: '5px',
          transition: 'width 0.3s',
        }} />
      </div>
      <span style={{ fontSize: '0.8rem', color: '#666', minWidth: '2.5rem', textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}

// Section showing a breakdown map (by grade, bread, etc.)
function BreakdownSection({ title, entries, total }) {
  if (!entries || entries.length === 0) return null;
  return (
    <div style={{ marginTop: '0.75rem' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>
        {title}
      </div>
      {entries.map(({ label, count }) => (
        <div key={label} style={{ display: 'grid', gridTemplateColumns: '7rem 1fr', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>
            {label}
          </span>
          <MiniBar value={count} max={total} />
        </div>
      ))}
    </div>
  );
}

function OrderStats() {
  const { schoolId } = useParams();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({}); // orderDate.id → bool

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`/api/admin/${schoolId}/orders/stats`);
      setStats(res.data || []);
      // Auto-expand the first (most recent) date that has orders.
      const first = (res.data || []).find(s => s.total_orders > 0);
      if (first) setExpanded({ [first.order_date.id]: true });
      setLoading(false);
    } catch (err) {
      setError('Failed to load stats');
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const toggleExpanded = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading stats…</div>;
  if (error) return <div style={{ padding: '2rem', color: '#dc3545' }}>{error}</div>;

  const totalDates = stats.length;
  const datesWithOrders = stats.filter(s => s.total_orders > 0).length;
  const totalOrders = stats.reduce((sum, s) => sum + s.total_orders, 0);
  const totalPaid = stats.reduce((sum, s) => sum + s.paid_orders, 0);
  const totalDeleted = stats.reduce((sum, s) => sum + (s.deleted_orders || 0), 0);

  return (
    <div style={{ maxWidth: '860px' }}>
      <h2 style={{ marginTop: 0 }}>📈 Order Stats</h2>
      <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-0.5rem' }}>
        Includes paid orders cleared with "Delete paid orders".
        {totalDeleted > 0 && ` Deleted unpaid orders (${totalDeleted}) are not counted.`}
      </p>

      {/* Summary row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem',
      }}>
        {[
          { label: 'Order Dates', value: totalDates },
          { label: 'Dates with Orders', value: datesWithOrders },
          { label: 'Total Orders', value: totalOrders },
          { label: 'Total Paid', value: totalPaid },
          { label: 'Total Unpaid', value: totalOrders - totalPaid },
          ...(totalDeleted > 0 ? [{ label: 'Deleted unpaid (not counted)', value: totalDeleted }] : []),
        ].map(({ label, value }) => (
          <div key={label} style={{
            background: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '1rem',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>{value}</div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>{label}</div>
          </div>
        ))}
      </div>

      {stats.length === 0 && (
        <p style={{ color: '#666' }}>No order dates yet.</p>
      )}

      {stats.map((s) => {
        const od = s.order_date;
        const isOpen = !!expanded[od.id];
        const unpaid = s.total_orders - s.paid_orders;
        const paidPct = s.total_orders > 0 ? Math.round((s.paid_orders / s.total_orders) * 100) : 0;

        return (
          <div key={od.id} style={{
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            overflow: 'hidden',
          }}>
            {/* Header row — always visible, clickable to expand */}
            <button
              onClick={() => toggleExpanded(od.id)}
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.9rem 1rem',
                display: 'grid',
                gridTemplateColumns: `1fr repeat(${s.deleted_orders > 0 ? 4 : 3}, auto)`,
                alignItems: 'center',
                gap: '1rem',
                textAlign: 'left',
                backgroundColor: isOpen ? '#f0f4ff' : '#fff',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {od.title || formatDate(od.delivery_date)}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#888' }}>
                  Delivery: {formatDate(od.delivery_date)}
                  {!od.is_active && <span style={{ marginLeft: '0.5rem', color: '#dc3545' }}>closed</span>}
                </div>
              </div>
              <div style={{ textAlign: 'center', minWidth: '4rem' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{s.total_orders}</div>
                <div style={{ fontSize: '0.7rem', color: '#666' }}>orders</div>
              </div>
              <div style={{ textAlign: 'center', minWidth: '4rem' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#28a745' }}>{s.paid_orders}</div>
                <div style={{ fontSize: '0.7rem', color: '#666' }}>paid</div>
              </div>
              <div style={{ textAlign: 'center', minWidth: '4rem' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: unpaid > 0 ? '#dc3545' : '#aaa' }}>{unpaid}</div>
                <div style={{ fontSize: '0.7rem', color: '#666' }}>unpaid</div>
              </div>
              {s.deleted_orders > 0 && (
                <div style={{ textAlign: 'center', minWidth: '4rem' }} title="Unpaid orders that were deleted — not counted">
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#888' }}>{s.deleted_orders}</div>
                  <div style={{ fontSize: '0.7rem', color: '#666' }}>deleted</div>
                </div>
              )}
            </button>

            {/* Expanded detail */}
            {isOpen && s.total_orders > 0 && (
              <div style={{ padding: '1rem', borderTop: '1px solid #dee2e6', background: '#fafbff' }}>
                {/* Paid progress bar */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.3rem' }}>
                    Payment: {s.paid_orders} paid / {unpaid} unpaid ({paidPct}%)
                    {s.archived_orders > 0 && ` · ${s.archived_orders} cleared from the Orders page`}
                  </div>
                  <div style={{
                    height: '12px', background: '#e9ecef', borderRadius: '6px', overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${paidPct}%`, height: '100%',
                      background: 'linear-gradient(90deg, #28a745, #20c997)',
                      borderRadius: '6px',
                    }} />
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1.5rem',
                }}>
                  <BreakdownSection title="By Grade" entries={sortedBreakdown(s.by_grade)} total={s.total_orders} />
                  <BreakdownSection title="Bread" entries={sortedBreakdown(s.by_bread)} total={s.total_orders} />
                  <BreakdownSection title="Meat" entries={sortedBreakdown(s.by_meat)} total={s.total_orders} />
                  <BreakdownSection title="Cookie" entries={sortedBreakdown(s.by_cookie)} total={s.total_orders} />
                  <BreakdownSection title="Side" entries={sortedBreakdown(s.by_side)} total={s.total_orders} />
                </div>
              </div>
            )}

            {isOpen && s.total_orders === 0 && (
              <div style={{ padding: '1rem', borderTop: '1px solid #dee2e6', color: '#999', fontSize: '0.9rem' }}>
                No orders for this date.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default OrderStats;
